import { usePresenceStore } from '../../../stores/prioritization/presenceStore';
import type { PrioritizationSession } from '../../../types/prioritization/session';
import { C } from '../theme/participantTokens';
import { LayerBack, LayerBar } from './layout/ParticipantLayout';

interface LobbyViewProps {
  session: PrioritizationSession;
  onStartRanking: () => void;
  onBack: () => void;
  /** S-3: names are hidden in this session. */
  hideNames?: boolean;
}

export function LobbyView({ session, onStartRanking, onBack, hideNames = false }: LobbyViewProps) {
  const presentUsers = usePresenceStore(s => s.presentUsers);
  const isConnected = usePresenceStore(s => s.isConnected);

  return (
    <>
      <LayerBar>
        <LayerBack label="Sessions" onClick={onBack} />
        <div style={{ padding: '0 16px', fontFamily: 'Spectral, Georgia, serif', fontSize: '0.88rem', color: C.textSec, borderRight: `1px solid ${C.rule}` }}>
          {session.name}
        </div>
        <div style={{ padding: '0 14px', fontFamily: 'JetBrains Mono', fontSize: '0.52rem', color: C.mustard, letterSpacing: '0.1em', display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 5, height: 5, borderRadius: '50%', background: isConnected ? C.mustard : C.textDim, flexShrink: 0 }} />
          {isConnected ? 'Connected' : 'Connecting…'}
        </div>
      </LayerBar>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <div style={{ padding: '28px 32px 48px', maxWidth: 860 }}>
          <div style={{ background: C.surface, border: `1px solid ${C.rule}`, borderLeft: '3px solid rgba(212,160,23,0.4)', borderRadius: 4, padding: '20px 22px', marginBottom: 14 }}>
            <div style={{ fontFamily: 'JetBrains Mono', fontSize: '0.58rem', letterSpacing: '0.25em', textTransform: 'uppercase', color: 'rgba(212,160,23,0.6)', marginBottom: 6 }}>
              Live Session · Lobby
            </div>
            <div style={{ fontFamily: 'Spectral, Georgia, serif', fontSize: '1.6rem', fontWeight: 600, color: C.textPri, marginBottom: 8 }}>
              {session.name}
            </div>
            <p style={{ fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif', fontSize: '0.85rem', fontWeight: 300, color: C.textTer, lineHeight: 1.6, margin: 0 }}>
              Waiting for the session to begin. You&apos;ll be notified when ranking starts.
            </p>
          </div>
          <div style={{ background: C.surface, border: `1px solid ${C.rule}`, borderRadius: 4, padding: '16px 18px', marginBottom: 14 }}>
            <div style={{ fontFamily: 'JetBrains Mono', fontSize: '0.58rem', fontWeight: 600, letterSpacing: '0.16em', color: C.textTer, textTransform: 'uppercase', marginBottom: 12, paddingBottom: 8, borderBottom: `1px solid ${C.rule}` }}>
              In the Room · {presentUsers.length} online
            </div>
            {!hideNames && <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {presentUsers.map(u => (
                <div key={u.userId} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '5px 10px', border: `1px solid ${C.ruleBright}`, borderRadius: 3 }}>
                  <span style={{ fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif', fontSize: '0.75rem', color: C.textSec }}>{u.userName ?? 'Unknown'}</span>
                </div>
              ))}
            </div>}
            {hideNames && (
              <div style={{ fontSize: '0.75rem', color: C.textSec }}>Names are hidden in this session.</div>
            )}
          </div>
          <button
            type="button"
            onClick={onStartRanking}
            style={{
              width: '100%', height: 40, background: 'none', border: `1px solid ${C.ruleBright}`,
              borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono', fontSize: '0.65rem',
              letterSpacing: '0.12em', textTransform: 'uppercase', color: C.textTer,
            }}
          >
            Continue to brief →
          </button>
        </div>
      </div>
    </>
  );
}
